import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Search, User, CalendarPlus, Receipt, Loader2, ArrowRight } from 'lucide-react';
import { useActiveClinic } from '@/lib/ActiveClinicContext';
import { usePatientsList } from '@/hooks/usePatients';
import { cn } from '@/utils/cn';

/**
 * Slim top bar with a patient search (Cmd/Ctrl+K) and a "+ New" quick-create
 * menu. Mirrors the productivity affordances clinic admins expect from a
 * Linear/Notion-style shell.
 */
export function TopBar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeClinic } = useActiveClinic();

  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const createMenuRef = useRef<HTMLDivElement>(null);
  const searchBoxRef = useRef<HTMLDivElement>(null);

  // Cmd/Ctrl+K — focus search
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
        setSearchOpen(true);
      } else if (e.key === 'Escape') {
        setSearchOpen(false);
        setCreateOpen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Click-outside to close pickers
  useEffect(() => {
    if (!searchOpen && !createOpen) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (searchOpen && searchBoxRef.current && !searchBoxRef.current.contains(target)) {
        setSearchOpen(false);
      }
      if (createOpen && createMenuRef.current && !createMenuRef.current.contains(target)) {
        setCreateOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [searchOpen, createOpen]);

  const trimmedSearch = search.trim();
  const patientsQuery = usePatientsList(activeClinic?.id ?? '', { search: trimmedSearch });
  const results = useMemo(() => {
    if (trimmedSearch.length < 2) return [];
    return (patientsQuery.data ?? []).slice(0, 8);
  }, [patientsQuery.data, trimmedSearch]);

  const goToPatient = (id: string) => {
    setSearch('');
    setSearchOpen(false);
    navigate(`/patients/${id}`);
  };

  const isMac = typeof navigator !== 'undefined' && /Mac/.test(navigator.platform);

  return (
    <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-gray-200 bg-page-light/80 px-6 py-3 backdrop-blur dark:border-gray-700 dark:bg-page-dark/80">
      {/* Search */}
      <div ref={searchBoxRef} className="relative w-full max-w-md">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            ref={searchInputRef}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setSearchOpen(true);
            }}
            onFocus={() => setSearchOpen(true)}
            placeholder={t('topbar.search_placeholder', 'Søg patient…')}
            className="block w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-14 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
          />
          <kbd className="pointer-events-none absolute right-2 top-1/2 hidden -translate-y-1/2 select-none items-center gap-0.5 rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 font-mono text-[10px] font-medium text-gray-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 sm:inline-flex">
            {isMac ? '⌘' : 'Ctrl'} K
          </kbd>
        </div>
        {searchOpen && trimmedSearch.length >= 2 && (
          <div className="absolute left-0 right-0 top-full z-30 mt-2 max-h-96 overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-600 dark:bg-gray-800">
            {patientsQuery.isLoading ? (
              <div className="flex items-center gap-2 px-3 py-3 text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('topbar.searching', 'Søger…')}
              </div>
            ) : results.length === 0 ? (
              <div className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">
                {t('topbar.no_matches', 'Ingen patienter fundet')}
              </div>
            ) : (
              <>
                <div className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                  {t('topbar.patients', 'Patienter')}
                </div>
                {results.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => goToPatient(p.id)}
                    className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium text-gray-900 dark:text-white">
                        {p.first_name} {p.last_name}
                        {p.pat_number && (
                          <span className="ml-2 font-mono text-xs text-gray-400">
                            #{p.pat_number}
                          </span>
                        )}
                      </div>
                      <div className="truncate text-xs text-gray-500 dark:text-gray-400">
                        {p.email ?? p.phone ?? '—'}
                      </div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-gray-400" />
                  </button>
                ))}
              </>
            )}
          </div>
        )}
      </div>

      <div className="flex-1" />

      {/* Quick-create */}
      <div ref={createMenuRef} className="relative">
        <button
          type="button"
          onClick={() => setCreateOpen((v) => !v)}
          className={cn(
            'inline-flex items-center gap-1 rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-500',
          )}
        >
          <Plus className="h-4 w-4" />
          {t('topbar.create', 'Opret')}
        </button>
        {createOpen && (
          <div className="absolute right-0 top-full z-30 mt-2 w-56 rounded-md border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-600 dark:bg-gray-800">
            <CreateMenuItem
              icon={<User className="h-4 w-4" />}
              label={t('topbar.new_patient', 'Ny patient')}
              onClick={() => {
                setCreateOpen(false);
                navigate('/patients/new');
              }}
            />
            <CreateMenuItem
              icon={<CalendarPlus className="h-4 w-4" />}
              label={t('topbar.new_appointment', 'Ny aftale')}
              onClick={() => {
                setCreateOpen(false);
                navigate('/appointments');
              }}
            />
            <CreateMenuItem
              icon={<Receipt className="h-4 w-4" />}
              label={t('topbar.new_invoice', 'Ny faktura')}
              onClick={() => {
                setCreateOpen(false);
                navigate('/invoices/new');
              }}
            />
          </div>
        )}
      </div>
    </header>
  );
}

function CreateMenuItem({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
    >
      <span className="text-gray-400">{icon}</span>
      {label}
    </button>
  );
}
